import { useMemo, useState } from 'react'
import { useStore } from '../store/store'
import type { BacktestMetrics } from '../domain/types'
import {
  DEFAULT_STOCK_PARAMS,
  backtestBets,
  backtestStock,
  type HistoricalBet,
  type StockStrategyType,
} from '../lib/math'
import { uid } from '../lib/id'
import { fmtCurrency, fmtPctValue, fmtSigned } from '../lib/format'
import { Banner, Button, Card, NumberField, PageHead, SelectField, Stat } from '../components/ui'
import { LineChart } from '../components/charts'

export function Backtest() {
  const assets = useStore((s) => s.assets)
  const predictions = useStore((s) => s.modelPredictions)
  const outcomes = useStore((s) => s.outcomes)
  const addBacktest = useStore((s) => s.addBacktest)

  const [assetId, setAssetId] = useState(assets[0]?.id ?? '')
  const [type, setType] = useState<StockStrategyType>('sma-cross')
  const [fast, setFast] = useState(20)
  const [slow, setSlow] = useState(50)
  const [fee, setFee] = useState(0)
  const [slippage, setSlippage] = useState(0.0005)
  const [spread, setSpread] = useState(0.0005)
  const [commission, setCommission] = useState(0)
  const [capital, setCapital] = useState(10000)

  const asset = assets.find((a) => a.id === assetId) ?? assets[0]

  const result = useMemo(() => {
    if (!asset) return null
    return backtestStock(
      asset.candles,
      { ...DEFAULT_STOCK_PARAMS, type, fast, slow, rsiPeriod: fast, breakoutLookback: fast, squeezePeriod: fast },
      { feePct: fee, slippagePct: slippage, spreadPct: spread, commissionPerTrade: commission },
      capital,
    )
  }, [asset, type, fast, slow, fee, slippage, spread, commission, capital])

  const betResult = useMemo(() => {
    const closingByRef = new Map(outcomes.map((o) => [o.refId, o.closingDecimal]))
    const bets: HistoricalBet[] = predictions
      .filter((p) => p.resolved && p.outcome !== undefined && p.marketProb > 0)
      .map((p) => ({ prob: p.estProb, decimalOdds: 1 / p.marketProb, won: p.outcome === 1, closingDecimal: closingByRef.get(p.id) }))
    return backtestBets(bets, { stakeFraction: 0.25, flat: false, feePct: 0, maxStakePct: 0.05 }, 10000)
  }, [predictions, outcomes])

  const save = () => {
    if (!result || !asset) return
    addBacktest({
      id: uid('bt'),
      name: `${asset.symbol} ${type}`,
      createdAt: new Date().toISOString(),
      strategy: type,
      params: { fast, slow, fee, slippage, spread, commission },
      metrics: result.metrics,
      inSample: result.inSample,
      outOfSample: result.outOfSample,
      equityCurve: result.equityCurve,
      overfitWarning: result.overfitWarning,
    })
  }

  if (!asset || !result) return <div className="empty">No assets loaded.</div>

  return (
    <div className="stack">
      <PageHead title="Backtesting">
        Test ideas on history with realistic costs. Separate in-sample from out-of-sample, and treat strong backtests with suspicion — markets adapt.
      </PageHead>

      <Card title="Stock strategy" subtitle="Long/flat signals, costs charged on every change of position">
        <div className="grid cols-4">
          <SelectField label="Asset" value={assetId} onChange={setAssetId} options={assets.map((a) => ({ value: a.id, label: a.symbol }))} />
          <SelectField label="Strategy" value={type} onChange={setType} options={[{ value: 'sma-cross', label: 'SMA crossover' }, { value: 'rsi-reversion', label: 'RSI reversion' }, { value: 'breakout', label: 'Breakout' }, { value: 'vol-squeeze', label: 'Volatility squeeze' }, { value: 'gap-fill', label: 'Gap fill' }, { value: 'momentum', label: 'Momentum' }]} />
          <NumberField label="Fast / lookback period" value={fast} onChange={setFast} />
          <NumberField label="Slow period" value={slow} onChange={setSlow} />
          <NumberField label="Fee %" value={fee} onChange={setFee} step={0.0005} />
          <NumberField label="Slippage %" value={slippage} onChange={setSlippage} step={0.0005} />
          <NumberField label="Spread %" value={spread} onChange={setSpread} step={0.0005} />
          <NumberField label="Commission / trade" value={commission} onChange={setCommission} step={0.5} />
          <NumberField label="Capital" value={capital} onChange={setCapital} step={1000} />
        </div>
      </Card>

      {result.overfitWarning && <Banner tone="warn"><strong>Overfit check:</strong> {result.overfitWarning}</Banner>}

      <div className="grid cols-4">
        <Stat label="Total return" value={fmtPctValue(result.metrics.totalReturnPct)} tone={result.metrics.totalReturnPct >= 0 ? 'pos' : 'neg'} />
        <Stat label="Max drawdown" value={fmtPctValue(result.metrics.maxDrawdownPct)} tone="neg" />
        <Stat label="Sharpe-like" value={result.metrics.sharpeLike.toFixed(2)} />
        <Stat label="Win rate" value={fmtPctValue(result.metrics.winRate * 100, 0)} />
      </div>

      <Card title="Equity curve" subtitle="Strategy applied across the full sample">
        <LineChart series={[{ values: result.equityCurve, color: 'var(--accent)', fill: true }]} height={220} yFormat={(n) => `$${n.toFixed(0)}`} />
      </Card>

      <div className="grid cols-2">
        <Card title="In-sample vs out-of-sample" subtitle="70/30 split — out-of-sample is what matters">
          <SampleTable label="In-sample (first 70%)" m={result.inSample} />
          <div className="divider" />
          <SampleTable label="Out-of-sample (last 30%)" m={result.outOfSample} />
        </Card>

        <Card title="Walk-forward segments" subtitle="Consistency across time beats a single great number">
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Segment</th><th>Return</th><th>Max DD</th><th>Trades</th></tr></thead>
              <tbody>
                {result.walkForward.map((w) => (
                  <tr key={w.label}>
                    <td>{w.label}</td>
                    <td className={`mono ${w.returnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(w.returnPct, 1)}%</td>
                    <td className="mono">{w.maxDrawdownPct.toFixed(1)}%</td>
                    <td className="mono">{w.trades}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button onClick={save}>Save backtest result</Button>
        </Card>
      </div>

      <Card title="Bet / market history backtest" subtitle={`Replays your ${predictions.filter((p) => p.resolved).length} resolved predictions with quarter-Kelly staking`}>
        <div className="grid cols-4">
          <Stat label="Ending bankroll" value={fmtCurrency(betResult.endingBankroll)} tone={betResult.endingBankroll >= 10000 ? 'pos' : 'neg'} />
          <Stat label="Total return" value={fmtPctValue(betResult.metrics.totalReturnPct)} tone={betResult.metrics.totalReturnPct >= 0 ? 'pos' : 'neg'} />
          <Stat label="Profit factor" value={Number.isFinite(betResult.metrics.profitFactor) ? betResult.metrics.profitFactor.toFixed(2) : '∞'} />
          <Stat label="Avg CLV" value={fmtSigned(betResult.avgClvPct, 1) + '%'} tone={betResult.avgClvPct >= 0 ? 'pos' : 'neg'} />
        </div>
        <LineChart series={[{ values: betResult.equityCurve, color: 'var(--info)', fill: true }]} height={180} yFormat={(n) => `$${n.toFixed(0)}`} />
      </Card>
    </div>
  )
}

function SampleTable({ label, m }: { label: string; m: BacktestMetrics }) {
  return (
    <div>
      <div className="uppercase faint" style={{ marginBottom: 6 }}>{label}</div>
      <div className="kv"><span className="k">Return</span><span className={`v ${m.totalReturnPct >= 0 ? 'pos' : 'neg'}`}>{fmtSigned(m.totalReturnPct, 1)}%</span></div>
      <div className="kv"><span className="k">Max drawdown</span><span className="v">{m.maxDrawdownPct.toFixed(1)}%</span></div>
      <div className="kv"><span className="k">Expectancy / trade</span><span className="v">{fmtCurrency(m.expectancy)}</span></div>
      <div className="kv"><span className="k">Profit factor</span><span className="v">{Number.isFinite(m.profitFactor) ? m.profitFactor.toFixed(2) : '∞'}</span></div>
      <div className="kv"><span className="k">Longest losing streak</span><span className="v">{m.longestLosingStreak}</span></div>
      <div className="kv"><span className="k">Trades</span><span className="v">{m.trades}</span></div>
    </div>
  )
}
